import { Suspense, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Route, Router, Switch } from "react-router-dom";
import { createBrowserHistory } from "history";
import jwtDecode from "jwt-decode";
import { createSelector } from "reselect";
import { toast, Toaster } from "sonner";

import { profile } from "src/apis/auth.api";
import { setAuthToken } from "src/utils/axios";

import { logout, setPermission, setUser } from "_common/actions/auth.action";
import { MainLayout } from "_components/layout";
import { AnonymusRoute, PrivateRoute } from "_components/routes";
import { Login } from "_modules/auth/pages";
import { E404 } from "_modules/error/pages";

import { CConfirm } from "./1/common/components/others";
import { isEmpty } from "./utils/funcs";
import { useConfirm } from "./zustand/confirm";

import "_assets/css/main.scss";

export const history = createBrowserHistory();

const selectAuth = createSelector(
  (state) => state.auth,
  ({ token }) => ({ token }),
);

export const confirm = ({
  title = "Xác nhận",
  content = "Hành động này sẽ không thể hoàn tác.",
  type = "error",
  onConfirm,
  confirmText = "Xác nhận",
  onCancel,
  cancelText = "Hủy",
}) => {
  useConfirm.getState().confirm({
    title,
    content,
    type,
    onConfirm,
    confirmText,
    onCancel,
    cancelText,
  });
};

export const warningDelete = ({
  title = "Bạn có chắc chắn xóa?",
  content = "Hành động này sẽ không thể hoàn tác.",
  loading = "Đang thực hiện, vui lòng chờ...",
  type = "error",
  onConfirm: onProceed,
  confirmText = "Xóa",
  cancelText = "Hủy",
  onSuccess,
  onError,
}) => {
  confirm({
    title,
    content,
    type,
    confirmText,
    cancelText,
    onConfirm: () =>
      toast.promise(onProceed, {
        loading,
        success: onSuccess,
        error: onError,
      }),
  });
};

const App = () => {
  const dispatch = useDispatch();
  const { token } = useSelector(selectAuth);

  useEffect(() => {
    try {
      const { exp } = jwtDecode(token);
      if (exp < Date.now() / 1000) dispatch({ type: "SET_AUTHENTICATE" });
      loading(true);
      // set Token
      setAuthToken(token);
      // load profile
      profile()
        .then((user) => {
          if (isEmpty(user.permissions)) return dispatch(logout());
          dispatch(setUser(user));
          dispatch(setPermission(user));
        })
        .catch((err) => dispatch({ type: "SET_AUTHENTICATE" }))
        .finally(() => loading(false));
    } catch (err) {
      loading(false);
      return dispatch(logout());
    }
  }, [profile]);

  return (
    <Suspense fallback={<></>}>
      <Router history={history}>
        <Switch>
          <AnonymusRoute
            exact
            path="/login"
            name="Login Page"
            render={(props) => <Login {...props} />}
          />
          <Route
            exact
            path="/404"
            name="Error 404"
            render={(props) => <E404 {...props} />}
          />
          <Route
            exact
            path="/500"
            name="Error 500"
            render={(props) => <E500 {...props} />}
          />
          <PrivateRoute
            path="/"
            name="Default"
            render={(props) => <MainLayout {...props} />}
          />
        </Switch>
      </Router>

      <CConfirm />
      <Toaster position="top-right" richColors />
    </Suspense>
  );
};

export default App;
